import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';

// Main settings card
const SettingsCard = styled.div`
  background: ${({ theme }) =>
    theme?.colors?.card?.background ||
    (theme?.name === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(255, 255, 255, 0.95)')};
  border: 1px solid
    ${({ theme }) =>
      theme?.colors?.card?.border ||
      (theme?.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgb(226, 232, 240)')};
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
`;

const SettingsTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => (theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 20px 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SettingsIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: ${({ theme }) =>
    theme.name === 'dark'
      ? 'rgba(250, 70, 22, 0.15)'
      : 'rgba(250, 70, 22, 0.08)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fa4616;

  svg {
    width: 18px;
    height: 18px;
  }
`;

// Toggle row for main POS enable/disable
const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  background: ${({ theme }) =>
    theme.name === 'dark'
      ? 'rgba(255, 255, 255, 0.03)'
      : 'rgba(0, 0, 0, 0.02)'};
  border-radius: 12px;
  margin-bottom: 24px;
`;

const ToggleInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const ToggleIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${({ theme }) =>
    theme.name === 'dark'
      ? 'rgba(250, 70, 22, 0.15)'
      : 'rgba(250, 70, 22, 0.08)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fa4616;

  svg {
    width: 24px;
    height: 24px;
  }
`;

const ToggleText = styled.div``;

const ToggleLabel = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => (theme.name === 'dark' ? '#ffffff' : '#0F172A')};
`;

const ToggleDescription = styled.div`
  font-size: 14px;
  color: ${({ theme }) => (theme.name === 'dark' ? '#94a3b8' : '#64748b')};
  margin-top: 4px;
`;

// Toggle switch
const Toggle = styled.label`
  position: relative;
  display: inline-block;
  width: 52px;
  height: 28px;
`;

const ToggleInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;

  &:checked + span {
    background: linear-gradient(135deg, #fa4616, #ff6b3b);
  }

  &:checked + span:before {
    transform: translateX(24px);
  }
`;

const ToggleSlider = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${({ theme }) =>
    theme.name === 'dark' ? 'rgba(255, 255, 255, 0.2)' : '#cbd5e1'};
  transition: all 0.3s ease;
  border-radius: 28px;

  &:before {
    position: absolute;
    content: '';
    height: 22px;
    width: 22px;
    left: 3px;
    top: 3px;
    background-color: #ffffff;
    transition: all 0.3s ease;
    border-radius: 50%;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
`;

// Settings grid layout
const SettingsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

// Two column grid for Receipt Delivery and Fee Settings row
const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  margin-top: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

// Option group (for radio-style selections)
const OptionGroup = styled.div`
  background: ${({ theme }) =>
    theme.name === 'dark'
      ? 'rgba(255, 255, 255, 0.03)'
      : 'rgba(0, 0, 0, 0.02)'};
  border-radius: 12px;
  padding: 20px;
  min-width: 0;
  overflow: hidden;
`;

const OptionGroupTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => (theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 16px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const OptionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const OptionItem = styled.label`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  background: ${({ theme, selected }) =>
    selected
      ? theme.name === 'dark'
        ? 'rgba(250, 70, 22, 0.15)'
        : 'rgba(250, 70, 22, 0.08)'
      : theme.name === 'dark'
      ? 'rgba(255, 255, 255, 0.03)'
      : '#ffffff'};
  border: 1px solid
    ${({ theme, selected }) =>
      selected
        ? '#fa4616'
        : theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgb(226, 232, 240)'};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: ${({ selected }) =>
      selected ? '#fa4616' : 'rgba(250, 70, 22, 0.5)'};
  }
`;

const RadioCircle = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid
    ${({ theme, selected }) =>
      selected
        ? '#fa4616'
        : theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.3)'
        : '#cbd5e1'};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  flex-shrink: 0;

  &::after {
    content: '';
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${({ selected }) => (selected ? '#fa4616' : 'transparent')};
    transition: all 0.2s;
  }
`;

const OptionLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => (theme.name === 'dark' ? '#ffffff' : '#0F172A')};
`;

const HiddenRadio = styled.input`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
`;

// Fee settings
const FeeInputGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FeeInputGroup = styled.div`
  min-width: 0;
`;

const FeeLabel = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => (theme.name === 'dark' ? '#e2e8f0' : '#374151')};
  margin-bottom: 8px;
`;

const FeeInput = styled.input`
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  padding: 12px 16px;
  background: ${({ theme }) =>
    theme.name === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#ffffff'};
  border: 1px solid
    ${({ theme }) =>
      theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.15)'
        : 'rgb(226, 232, 240)'};
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
  color: ${({ theme }) => (theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #fa4616;
    box-shadow: 0 0 0 3px rgba(250, 70, 22, 0.1);
  }

  &::placeholder {
    color: ${({ theme }) => (theme.name === 'dark' ? '#64748b' : '#94a3b8')};
  }
`;

// Disabled overlay for when POS is off
const DisabledOverlay = styled.div`
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
  transition: opacity 0.3s ease;
`;

// Icons
const POSIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="2" y="3" width="20" height="14" rx="2" />
    <path d="M8 21h8" />
    <path d="M12 17v4" />
    <path d="M6 8h.01" />
    <path d="M10 8h.01" />
    <path d="M14 8h.01" />
    <path d="M18 8h.01" />
    <path d="M6 12h.01" />
    <path d="M10 12h.01" />
    <path d="M14 12h.01" />
    <path d="M18 12h.01" />
  </svg>
);

const PaymentIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
    <line x1="1" y1="10" x2="23" y2="10" />
  </svg>
);

const POSTab = ({
  posSettings = {},
  pos = false,
  feeSettings = {},
  setFieldValue,
}) => {
  const handlePosToggle = (checked) => {
    setFieldValue('pos', checked);
  };

  const handleSettingChange = (setting, value) => {
    setFieldValue('pos_settings', {
      ...posSettings,
      [setting]: value,
    });
  };

  const handleFeeChange = (field, value) => {
    setFieldValue(field, value);
  };

  return (
    <>
      {/* Main POS Toggle */}
      <ToggleRow>
        <ToggleInfo>
          <ToggleIcon>
            <POSIcon />
          </ToggleIcon>
          <ToggleText>
            <ToggleLabel>Uses Point of Sale</ToggleLabel>
            <ToggleDescription>
              Enable point of sale functionality for in-person ticket sales and
              payments
            </ToggleDescription>
          </ToggleText>
        </ToggleInfo>
        <Toggle>
          <ToggleInput
            type="checkbox"
            checked={pos}
            onChange={(e) => handlePosToggle(e.target.checked)}
          />
          <ToggleSlider />
        </Toggle>
      </ToggleRow>

      {/* POS Settings - Only shown when POS is enabled */}
      <DisabledOverlay disabled={!pos}>
        <SettingsCard>
          <SettingsTitle>
            <SettingsIcon>
              <PaymentIcon />
            </SettingsIcon>
            POS Configuration
          </SettingsTitle>

          <SettingsGrid>
            {/* Accepted Payment Methods */}
            <OptionGroup>
              <OptionGroupTitle>Accepted Payment Methods</OptionGroupTitle>
              <OptionList>
                {[
                  { value: 'both', label: 'Both' },
                  { value: 'cash', label: 'Cash' },
                  { value: 'card', label: 'Card' },
                ].map((option) => (
                  <OptionItem
                    key={option.value}
                    selected={posSettings?.payment === option.value}
                  >
                    <HiddenRadio
                      type="radio"
                      name="payment_method"
                      value={option.value}
                      checked={posSettings?.payment === option.value}
                      onChange={() =>
                        handleSettingChange('payment', option.value)
                      }
                    />
                    <RadioCircle
                      selected={posSettings?.payment === option.value}
                    />
                    <OptionLabel>{option.label}</OptionLabel>
                  </OptionItem>
                ))}
              </OptionList>
            </OptionGroup>

            {/* Cash Change Calculator */}
            <OptionGroup>
              <OptionGroupTitle>Show Cash Change Calculator</OptionGroupTitle>
              <OptionList>
                {[
                  { value: true, label: 'Yes' },
                  { value: false, label: 'No' },
                ].map((option) => (
                  <OptionItem
                    key={String(option.value)}
                    selected={posSettings?.calculator === option.value}
                  >
                    <HiddenRadio
                      type="radio"
                      name="calculator"
                      value={String(option.value)}
                      checked={posSettings?.calculator === option.value}
                      onChange={() =>
                        handleSettingChange('calculator', option.value)
                      }
                    />
                    <RadioCircle
                      selected={posSettings?.calculator === option.value}
                    />
                    <OptionLabel>{option.label}</OptionLabel>
                  </OptionItem>
                ))}
              </OptionList>
            </OptionGroup>

            {/* Ticket Delivery */}
            <OptionGroup>
              <OptionGroupTitle>Ticket Delivery</OptionGroupTitle>
              <OptionList>
                {[
                  { value: 'mobile', label: 'Mobile Delivery' },
                  { value: 'print', label: 'Print from POS' },
                  { value: 'physical', label: 'Physical Tickets' },
                ].map((option) => (
                  <OptionItem
                    key={option.value}
                    selected={posSettings?.ticket_delivery === option.value}
                  >
                    <HiddenRadio
                      type="radio"
                      name="ticket_delivery"
                      value={option.value}
                      checked={posSettings?.ticket_delivery === option.value}
                      onChange={() =>
                        handleSettingChange('ticket_delivery', option.value)
                      }
                    />
                    <RadioCircle
                      selected={posSettings?.ticket_delivery === option.value}
                    />
                    <OptionLabel>{option.label}</OptionLabel>
                  </OptionItem>
                ))}
              </OptionList>
            </OptionGroup>
          </SettingsGrid>

          {/* Second row for Receipt Delivery and Fee Settings */}
          <TwoColumnGrid>
            <OptionGroup>
              <OptionGroupTitle>Receipt Delivery</OptionGroupTitle>
              <OptionList>
                {[
                  { value: 'mobile', label: 'Mobile Delivery' },
                  { value: 'print', label: 'Print from POS' },
                  {
                    value: 'print_w_tickets',
                    label: 'Print from POS w/ Tickets',
                  },
                  { value: 'none', label: 'None' },
                ].map((option) => (
                  <OptionItem
                    key={option.value}
                    selected={posSettings?.receipt_delivery === option.value}
                  >
                    <HiddenRadio
                      type="radio"
                      name="receipt_delivery"
                      value={option.value}
                      checked={posSettings?.receipt_delivery === option.value}
                      onChange={() =>
                        handleSettingChange('receipt_delivery', option.value)
                      }
                    />
                    <RadioCircle
                      selected={posSettings?.receipt_delivery === option.value}
                    />
                    <OptionLabel>{option.label}</OptionLabel>
                  </OptionItem>
                ))}
              </OptionList>
            </OptionGroup>

            {/* Fee Settings */}
            <OptionGroup>
              <OptionGroupTitle>Default Fee Settings</OptionGroupTitle>
              <FeeInputGrid>
                <FeeInputGroup>
                  <FeeLabel htmlFor="pos_fee_dollar">
                    Dollar Amount (POS)
                  </FeeLabel>
                  <FeeInput
                    id="pos_fee_dollar"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={feeSettings?.pos_fee_dollar ?? ''}
                    onChange={(e) =>
                      handleFeeChange('pos_fee_dollar', e.target.value)
                    }
                  />
                </FeeInputGroup>
                <FeeInputGroup>
                  <FeeLabel htmlFor="pos_fee_percent">
                    Fee Percent (POS)
                  </FeeLabel>
                  <FeeInput
                    id="pos_fee_percent"
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    placeholder="0.00"
                    value={feeSettings?.pos_fee_percent ?? ''}
                    onChange={(e) =>
                      handleFeeChange('pos_fee_percent', e.target.value)
                    }
                  />
                </FeeInputGroup>
              </FeeInputGrid>
            </OptionGroup>
          </TwoColumnGrid>
        </SettingsCard>
      </DisabledOverlay>
    </>
  );
};

POSTab.propTypes = {
  posSettings: PropTypes.shape({
    payment: PropTypes.string,
    calculator: PropTypes.bool,
    ticket_delivery: PropTypes.string,
    receipt_delivery: PropTypes.string,
  }),
  pos: PropTypes.bool,
  feeSettings: PropTypes.shape({
    pos_fee_dollar: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    pos_fee_percent: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  setFieldValue: PropTypes.func.isRequired,
};

export default POSTab;
